import { useCallback, useState } from 'react';

export const useBoardMapController = () => {
  const [mapZoom, setMapZoom] = useState(1);
  const [mapTool, setMapTool] = useState('select');
  const [mapMeasureMode, setMapMeasureMode] = useState('measure');
  const [mapMeasureScales, setMapMeasureScales] = useState([]);
  const [mapActiveMeasureScaleIds, setMapActiveMeasureScaleIds] = useState([]);
  const [mapToggleMeasureScaleCommand, setMapToggleMeasureScaleCommand] =
    useState(null);
  const [mapToggleMeasureScaleCommandTick, setMapToggleMeasureScaleCommandTick] =
    useState(0);
  const [mapShapeType, setMapShapeType] = useState('circle');
  const [mapInteractionMode, setMapInteractionMode] = useState('pan');
  const [mapZoomCommand, setMapZoomCommand] = useState(null);
  const [mapZoomCommandTick, setMapZoomCommandTick] = useState(0);
  const [mapCanShowColorPalette, setMapCanShowColorPalette] = useState(false);
  const [mapCanShowLineOptions, setMapCanShowLineOptions] = useState(false);
  const [mapSelectedPinColor, setMapSelectedPinColor] = useState('secondary');
  const [mapPinColorCommand, setMapPinColorCommand] = useState('secondary');
  const [mapPinColorCommandTick, setMapPinColorCommandTick] = useState(0);
  const [mapSelectedPinSize, setMapSelectedPinSize] = useState('md');
  const [mapPinSizeCommand, setMapPinSizeCommand] = useState('md');
  const [mapPinSizeCommandTick, setMapPinSizeCommandTick] = useState(0);
  const [mapLinkActionMode, setMapLinkActionMode] = useState('none');
  const [mapSelectedPinIconSlug, setMapSelectedPinIconSlug] = useState(null);
  const [mapPinIconCommand, setMapPinIconCommand] = useState(null);
  const [mapPinIconCommandTick, setMapPinIconCommandTick] = useState(0);
  const [mapUnlinkItemCommandTick, setMapUnlinkItemCommandTick] = useState(0);
  const [mapOpenLinkModalCommandTick, setMapOpenLinkModalCommandTick] = useState(0);
  const [mapOpenBackgroundModalCommandTick, setMapOpenBackgroundModalCommandTick] =
    useState(0);
  const [mapOpenGridSettingsModalCommandTick, setMapOpenGridSettingsModalCommandTick] =
    useState(0);
  const [mapToggleGridVisibleCommandTick, setMapToggleGridVisibleCommandTick] =
    useState(0);
  const [mapCanShowRegionStyleOptions, setMapCanShowRegionStyleOptions] =
    useState(false);
  const [mapSelectedRegionColor, setMapSelectedRegionColor] = useState('secondary');
  const [mapSelectedRegionBorderStyle, setMapSelectedRegionBorderStyle] =
    useState('solid');
  const [mapRegionColorCommand, setMapRegionColorCommand] = useState('secondary');
  const [mapRegionColorCommandTick, setMapRegionColorCommandTick] = useState(0);
  const [mapRegionBorderStyleCommand, setMapRegionBorderStyleCommand] =
    useState('solid');
  const [mapRegionBorderStyleCommandTick, setMapRegionBorderStyleCommandTick] =
    useState(0);
  const [mapCanShowShapeColorOptions, setMapCanShowShapeColorOptions] =
    useState(false);
  const [mapCanShowShapeLayerOptions, setMapCanShowShapeLayerOptions] =
    useState(false);
  const [mapSelectedShapeType, setMapSelectedShapeType] = useState('circle');
  const [mapSelectedShapeColor, setMapSelectedShapeColor] = useState('secondary');
  const [mapSelectedShapeBorderStyle, setMapSelectedShapeBorderStyle] =
    useState('solid');
  const [mapShapeTypeCommand, setMapShapeTypeCommand] = useState('circle');
  const [mapShapeTypeCommandTick, setMapShapeTypeCommandTick] = useState(0);
  const [mapShapeColorCommand, setMapShapeColorCommand] = useState('secondary');
  const [mapShapeColorCommandTick, setMapShapeColorCommandTick] = useState(0);
  const [mapShapeLayerCommand, setMapShapeLayerCommand] = useState(null);
  const [mapShapeLayerCommandTick, setMapShapeLayerCommandTick] = useState(0);
  const [mapCanShowDrawingColorOptions, setMapCanShowDrawingColorOptions] =
    useState(false);
  const [mapSelectedDrawingColor, setMapSelectedDrawingColor] = useState('secondary');
  const [mapSelectedDrawingBorderStyle, setMapSelectedDrawingBorderStyle] =
    useState('solid');
  const [mapDrawingColorCommand, setMapDrawingColorCommand] = useState('secondary');
  const [mapDrawingColorCommandTick, setMapDrawingColorCommandTick] = useState(0);
  const [mapDrawingBorderStyleCommand, setMapDrawingBorderStyleCommand] =
    useState('solid');
  const [mapDrawingBorderStyleCommandTick, setMapDrawingBorderStyleCommandTick] =
    useState(0);
  const [mapSelectedLineColor, setMapSelectedLineColor] = useState('secondary');
  const [mapSelectedLineArrows, setMapSelectedLineArrows] = useState('none');
  const [mapSelectedLineBorderStyle, setMapSelectedLineBorderStyle] =
    useState('solid');
  const [mapLineColorCommand, setMapLineColorCommand] = useState('secondary');
  const [mapLineColorCommandTick, setMapLineColorCommandTick] = useState(0);
  const [mapLineArrowsCommand, setMapLineArrowsCommand] = useState('none');
  const [mapLineArrowsCommandTick, setMapLineArrowsCommandTick] = useState(0);
  const [mapCanCreateGroup, setMapCanCreateGroup] = useState(false);
  const [mapCanEnterGroup, setMapCanEnterGroup] = useState(false);
  const [mapCanUngroup, setMapCanUngroup] = useState(false);
  const [mapIsInsideGroup, setMapIsInsideGroup] = useState(false);
  const [mapActiveGroupId, setMapActiveGroupId] = useState(null);
  const [mapActiveGroupLabel, setMapActiveGroupLabel] = useState('');
  const [mapGroupCommand, setMapGroupCommand] = useState(null);
  const [mapGroupCommandTick, setMapGroupCommandTick] = useState(0);

  const dispatchMapZoomCommand = useCallback(command => {
    setMapZoomCommand(command);
    setMapZoomCommandTick(prev => prev + 1);
  }, []);

  const dispatchMapPinSize = useCallback(size => {
    setMapSelectedPinSize(size);
    setMapPinSizeCommand(size);
    setMapPinSizeCommandTick(prev => prev + 1);
  }, []);

  const dispatchMapPinIcon = useCallback(slug => {
    setMapSelectedPinIconSlug(slug || null);
    setMapPinIconCommand(slug || null);
    setMapPinIconCommandTick(prev => prev + 1);
  }, []);

  const dispatchMapUnlinkItemCommand = useCallback(() => {
    setMapUnlinkItemCommandTick(prev => prev + 1);
  }, []);

  const dispatchMapOpenLinkModalCommand = useCallback(() => {
    setMapOpenLinkModalCommandTick(prev => prev + 1);
  }, []);

  const dispatchMapOpenBackgroundModalCommand = useCallback(() => {
    setMapOpenBackgroundModalCommandTick(prev => prev + 1);
  }, []);

  const dispatchMapOpenGridSettingsModalCommand = useCallback(() => {
    setMapOpenGridSettingsModalCommandTick(prev => prev + 1);
  }, []);

  const dispatchMapToggleGridVisibleCommand = useCallback(() => {
    setMapToggleGridVisibleCommandTick(prev => prev + 1);
  }, []);

  const dispatchMapShapeType = useCallback(type => {
    setMapShapeType(type);
    setMapSelectedShapeType(type);
    setMapShapeTypeCommand(type);
    setMapShapeTypeCommandTick(prev => prev + 1);
  }, []);

  const dispatchMapShapeLayer = useCallback(command => {
    setMapShapeLayerCommand(command);
    setMapShapeLayerCommandTick(prev => prev + 1);
  }, []);

  const dispatchMapSelectionColor = useCallback(color => {
    setMapSelectedPinColor(color);
    setMapPinColorCommand(color);
    setMapPinColorCommandTick(prev => prev + 1);
    setMapSelectedShapeColor(color);
    setMapShapeColorCommand(color);
    setMapShapeColorCommandTick(prev => prev + 1);
    setMapSelectedRegionColor(color);
    setMapRegionColorCommand(color);
    setMapRegionColorCommandTick(prev => prev + 1);
    setMapSelectedDrawingColor(color);
    setMapDrawingColorCommand(color);
    setMapDrawingColorCommandTick(prev => prev + 1);
    setMapSelectedLineColor(color);
    setMapLineColorCommand(color);
    setMapLineColorCommandTick(prev => prev + 1);
  }, []);

  const dispatchMapLineArrows = useCallback(arrows => {
    setMapSelectedLineArrows(arrows);
    setMapLineArrowsCommand(arrows);
    setMapLineArrowsCommandTick(prev => prev + 1);
  }, []);

  const dispatchMapSelectionBorderStyle = useCallback(style => {
    setMapSelectedShapeBorderStyle(style);
    setMapSelectedRegionBorderStyle(style);
    setMapRegionBorderStyleCommand(style);
    setMapRegionBorderStyleCommandTick(prev => prev + 1);
    setMapSelectedDrawingBorderStyle(style);
    setMapDrawingBorderStyleCommand(style);
    setMapDrawingBorderStyleCommandTick(prev => prev + 1);
    setMapSelectedLineBorderStyle(style);
  }, []);

  const handleMapInteractionModeChange = useCallback(nextMode => {
    setMapInteractionMode(nextMode);
    setMapTool('select');
  }, []);

  const dispatchMapGroupCommand = useCallback(command => {
    setMapGroupCommand(command);
    setMapGroupCommandTick(prev => prev + 1);
  }, []);

  const dispatchMapToggleMeasureScale = useCallback(scaleId => {
    if (!scaleId) {
      return;
    }
    setMapActiveMeasureScaleIds(prev =>
      prev.includes(scaleId)
        ? prev.filter(id => id !== scaleId)
        : [...prev, scaleId],
    );
    setMapToggleMeasureScaleCommand(scaleId);
    setMapToggleMeasureScaleCommandTick(prev => prev + 1);
  }, []);

  return {
    mapZoom,
    setMapZoom,
    mapTool,
    setMapTool,
    mapMeasureMode,
    setMapMeasureMode,
    mapMeasureScales,
    setMapMeasureScales,
    mapActiveMeasureScaleIds,
    setMapActiveMeasureScaleIds,
    mapToggleMeasureScaleCommand,
    mapToggleMeasureScaleCommandTick,
    mapShapeType,
    mapInteractionMode,
    mapZoomCommand,
    mapZoomCommandTick,
    mapCanShowColorPalette,
    setMapCanShowColorPalette,
    mapCanShowLineOptions,
    setMapCanShowLineOptions,
    mapSelectedPinColor,
    setMapSelectedPinColor,
    mapPinColorCommand,
    mapPinColorCommandTick,
    mapSelectedPinSize,
    setMapSelectedPinSize,
    mapPinSizeCommand,
    mapPinSizeCommandTick,
    mapLinkActionMode,
    setMapLinkActionMode,
    mapSelectedPinIconSlug,
    setMapSelectedPinIconSlug,
    mapPinIconCommand,
    mapPinIconCommandTick,
    mapUnlinkItemCommandTick,
    mapOpenLinkModalCommandTick,
    mapOpenBackgroundModalCommandTick,
    mapOpenGridSettingsModalCommandTick,
    mapToggleGridVisibleCommandTick,
    mapCanShowRegionStyleOptions,
    setMapCanShowRegionStyleOptions,
    mapSelectedRegionColor,
    setMapSelectedRegionColor,
    mapSelectedRegionBorderStyle,
    setMapSelectedRegionBorderStyle,
    mapRegionColorCommand,
    mapRegionColorCommandTick,
    mapRegionBorderStyleCommand,
    mapRegionBorderStyleCommandTick,
    mapCanShowShapeColorOptions,
    setMapCanShowShapeColorOptions,
    mapCanShowShapeLayerOptions,
    setMapCanShowShapeLayerOptions,
    mapSelectedShapeType,
    setMapSelectedShapeType,
    mapSelectedShapeColor,
    setMapSelectedShapeColor,
    mapSelectedShapeBorderStyle,
    setMapSelectedShapeBorderStyle,
    mapShapeTypeCommand,
    mapShapeTypeCommandTick,
    mapShapeColorCommand,
    mapShapeColorCommandTick,
    mapShapeLayerCommand,
    mapShapeLayerCommandTick,
    mapCanShowDrawingColorOptions,
    setMapCanShowDrawingColorOptions,
    mapSelectedDrawingColor,
    setMapSelectedDrawingColor,
    mapSelectedDrawingBorderStyle,
    setMapSelectedDrawingBorderStyle,
    mapDrawingColorCommand,
    mapDrawingColorCommandTick,
    mapDrawingBorderStyleCommand,
    mapDrawingBorderStyleCommandTick,
    mapSelectedLineColor,
    setMapSelectedLineColor,
    mapSelectedLineArrows,
    setMapSelectedLineArrows,
    mapSelectedLineBorderStyle,
    setMapSelectedLineBorderStyle,
    mapLineColorCommand,
    mapLineColorCommandTick,
    mapLineArrowsCommand,
    mapLineArrowsCommandTick,
    mapCanCreateGroup,
    setMapCanCreateGroup,
    mapCanEnterGroup,
    setMapCanEnterGroup,
    mapCanUngroup,
    setMapCanUngroup,
    mapIsInsideGroup,
    setMapIsInsideGroup,
    mapActiveGroupId,
    setMapActiveGroupId,
    mapActiveGroupLabel,
    setMapActiveGroupLabel,
    mapGroupCommand,
    mapGroupCommandTick,
    dispatchMapZoomCommand,
    dispatchMapPinSize,
    dispatchMapPinIcon,
    dispatchMapUnlinkItemCommand,
    dispatchMapOpenLinkModalCommand,
    dispatchMapOpenBackgroundModalCommand,
    dispatchMapOpenGridSettingsModalCommand,
    dispatchMapToggleGridVisibleCommand,
    dispatchMapShapeType,
    dispatchMapShapeLayer,
    dispatchMapSelectionColor,
    dispatchMapSelectionBorderStyle,
    dispatchMapLineArrows,
    dispatchMapGroupCommand,
    dispatchMapToggleMeasureScale,
    handleMapInteractionModeChange,
  };
};
